import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface DrawerItem {
  isOpen: boolean;
}

// Type guard function to check if a value is a DrawerItem
function isDrawerItem(value: any): value is DrawerItem {
  return value && typeof value === 'object' && 'isOpen' in value;
}

export interface DrawerState {
  lastOpenDrawerId?: string;
  previousDrawerId?: string;
  [key: string]: DrawerItem | string | undefined;
}

export const initialDrawerState: DrawerState = {
  lastOpenDrawerId: undefined,
  previousDrawerId: undefined,
};

interface DrawerPayloadWithId {
  id: string;
}

const drawerSlice = createSlice({
  name: 'drawers',
  initialState: initialDrawerState,
  reducers: {
    open(state, action: PayloadAction<DrawerPayloadWithId>) {
      const { id } = action.payload;

      // Always store the current drawer as the previous drawer before updating
      // Only if it's different from the new drawer being opened
      if (state.lastOpenDrawerId && state.lastOpenDrawerId !== id) {
        state.previousDrawerId = state.lastOpenDrawerId;
      }

      // Update the last open drawer ID
      state.lastOpenDrawerId = id;

      // Initialize or just set isOpen to true
      state[id] = { isOpen: true };
    },

    // Action to explicitly set the last open drawer ID
    setLastOpenDrawer(state, action: PayloadAction<{ id: string }>) {
      const { id } = action.payload;
      state.lastOpenDrawerId = id;
    },

    close(state, action: PayloadAction<DrawerPayloadWithId>) {
      const { id } = action.payload;
      const drawer = state[id];
      if (drawer && isDrawerItem(drawer)) {
        drawer.isOpen = false;
      }
    },
    toggle(state, action: PayloadAction<DrawerPayloadWithId>) {
      const { id } = action.payload;
      if (!state[id]) {
        // Initialize if it doesn't exist, start open
        state[id] = { isOpen: true };
      } else {
        const drawer = state[id];
        if (isDrawerItem(drawer)) {
          // Toggle existing state
          drawer.isOpen = !drawer.isOpen;
        }
      }
    },
    closeAll(state) {
      Object.keys(state).forEach((drawerId) => {
        if (
          drawerId !== 'lastOpenDrawerId' &&
          drawerId !== 'previousDrawerId'
        ) {
          const drawer = state[drawerId];
          if (drawer && isDrawerItem(drawer)) {
            drawer.isOpen = false;
          }
        }
      });
    },
    closeAllBut(state, action: PayloadAction<DrawerPayloadWithId>) {
      const { id: currentId } = action.payload;

      // If we're switching to a different drawer, store the current one as previous
      if (state.lastOpenDrawerId && state.lastOpenDrawerId !== currentId) {
        state.previousDrawerId = state.lastOpenDrawerId;
      }

      // Store the current drawer as the last open drawer
      state.lastOpenDrawerId = currentId;

      Object.keys(state).forEach((drawerId) => {
        if (
          drawerId !== currentId &&
          drawerId !== 'lastOpenDrawerId' &&
          drawerId !== 'previousDrawerId'
        ) {
          const drawer = state[drawerId];
          if (drawer && isDrawerItem(drawer)) {
            drawer.isOpen = false;
          }
        }
      });

      // Ensure the current one is marked as open, creating if necessary
      state[currentId] = { isOpen: true };
    },

    // Action to reopen the previous drawer
    reopenLastDrawer(state) {
      const previousDrawerId = state.previousDrawerId;

      if (previousDrawerId) {
        // If we're switching to the previous drawer, update the drawer history
        if (
          state.lastOpenDrawerId &&
          state.lastOpenDrawerId !== previousDrawerId
        ) {
          // The current drawer becomes the previous drawer
          state.previousDrawerId = state.lastOpenDrawerId;
          // The previous drawer becomes the current drawer
          state.lastOpenDrawerId = previousDrawerId;
        }

        // Close all drawers first
        Object.keys(state).forEach((drawerId) => {
          if (
            drawerId !== 'lastOpenDrawerId' &&
            drawerId !== 'previousDrawerId'
          ) {
            const drawer = state[drawerId];
            if (drawer && isDrawerItem(drawer)) {
              drawer.isOpen = false;
            }
          }
        });

        // Open the previous drawer
        state[previousDrawerId] = { isOpen: true };
      }
    },
  },
});

// Export generated action creators
export const {
  open: openDrawer,
  close: closeDrawer,
  toggle: toggleDrawer,
  closeAll: closeAllDrawers,
  closeAllBut: closeAllButCurrent,
  reopenLastDrawer,
  setLastOpenDrawer: setLastOpenDrawerId,
} = drawerSlice.actions;

// Export the reducer
export const drawerReducer = drawerSlice.reducer;
